import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { SensorCableSlotExcelStyle } from 'icreate/common/models/sensor-cable-tag';
import { SensorCableSheetStylesApiService } from './sensor-cable-sheet-styles-API.service';
import { SlotOptionType, terminalType, TerminalType } from 'icreate/common/models/slot';
import { getColumnWithBackground, isDOSlot, isThermalK } from '@models/sensor-cable-tag-sheet';
import {
    excelAIColumnLength,
    excelDIColumnLength,
    excelDONormalColumnLength,
    excelDORelayColumnLength,
    excelOutputColumnLength,
    excelThermalColumnLength,
} from './sensor-to-excel-utils';
import {
    DIRows,
    DONormalRows,
    DORelayRows,
    AIRows,
    OutputRows,
    ThermalKRows,
    ComWireRows,
} from 'icreate/common/models/sensor-cable-tag-print';

@Injectable({
    providedIn: 'root',
})
export class SensorCableExcelStyleService {
    readonly DEFAULT_FAMILY_FONT = 'Montserrat';
    readonly DEFAULT_FONT_SIZE = '14px';

    private sensorStyles: SensorCableSlotExcelStyle;
    private defaultStyle: { [key: string]: string };

    constructor(private sensorSheetStylesService: SensorCableSheetStylesApiService) {}

    async setSlotStyles(defaultStyle, slotId, outputSelector) {
        const savedStyles = await this.sensorSheetStylesService.getSlotStyle(slotId, outputSelector);
        if (savedStyles && savedStyles.styles) {
            const newStyle = this.applySavedStyles(savedStyles.styles, defaultStyle);
            this.sensorStyles = {
                id: savedStyles.id,
                slotId,
                styles: newStyle,
                outputType: outputSelector,
            };
            return newStyle;
        } else {
            this.sensorStyles = { slotId, styles: defaultStyle, outputType: outputSelector };
        }
    }

    areStylesUpdated(currentStyles): boolean {
        const hasDifferences = this.hasStyleDifference(currentStyles);
        return hasDifferences;
    }

    updateToolbar(selectedCellsStyles, spreadsheet) {
        this.updateBoldToolbarStyle(selectedCellsStyles, spreadsheet);
        this.updateAlignToolbarStyle(selectedCellsStyles, spreadsheet);
        this.updateFamilyFont(selectedCellsStyles, spreadsheet);
        this.updateFontSize(selectedCellsStyles, spreadsheet);
        this.updateTextColor(selectedCellsStyles);
        this.updateCellColor(selectedCellsStyles);
    }

    getSelectedCells(coords) {
        const [x1, y1, x2, y2] = coords;
        const selectedCells = [];

        for (let x = Number(x1); x <= Number(x2); x++) {
            if (x === 0) continue;
            const columnLetter = this.columnToLetter(x);
            for (let y = Number(y1); y <= Number(y2); y++) {
                selectedCells.push(columnLetter + (y + 1));
            }
        }

        return selectedCells;
    }

    setDefaultStyle(worksheet, slotType: SlotOptionType, outputType: TerminalType) {
        const columnsWithBackground = getColumnWithBackground(slotType, outputType);
        const rowCountMap: Record<string, number> = {
            DI: DIRows,
            'DO Normal': DONormalRows,
            'DO Relay': DORelayRows * 3,
            'AI (4-20mA)': AIRows,
            'AI (Pt100 2-wire)': AIRows,
            'AI (1-5V)': AIRows,
            'AI (Pt100 3-wire)': AIRows * 3,
            'AO (4-20mA)': OutputRows,
            'AI (Thermal K)': ThermalKRows + ComWireRows,
        };
        const columnLengthMap: Record<string, number> = {
            DI: excelDIColumnLength,
            'DO Normal': excelDONormalColumnLength,
            'DO Relay': excelDORelayColumnLength,
            'AI (4-20mA)': excelAIColumnLength,
            'AI (1-5V)': excelAIColumnLength,
            'AI (Pt100 2-wire)': excelAIColumnLength,
            'AI (Pt100 3-wire)': excelAIColumnLength,
            'AO (4-20mA)': excelOutputColumnLength,
            'AI (Thermal K)': excelThermalColumnLength,
        };

        if (isDOSlot(slotType) && outputType == terminalType.normal) slotType = 'DO Normal' as any;
        if (isDOSlot(slotType) && outputType == terminalType.relay) slotType = 'DO Relay' as any;

        const totalRows = rowCountMap[slotType];
        const totalColumns = columnLengthMap[slotType];
        const toLetter = (i: number) => String.fromCharCode(65 + i);
        for (let col = 0; col < totalColumns; col++) {
            for (let row = 0; row <= totalRows; row++) {
                const cell = `${toLetter(col)}${row}`;
                const backgroundColor = columnsWithBackground.has(col) ? '#eee' : '#ffff';
                const color = columnsWithBackground.has(col) ? '#aeaeae' : '#000';
                worksheet.setStyle(cell, 'background-color', backgroundColor, true);
                worksheet.setStyle(cell, 'color', color, true);
            }
        }
        this.defaultStyle = worksheet.getStyle();
    }

    async saveStyleDifferences(currentStyles) {
        const styleDifferences = this.getStylesDifferences(currentStyles);
        try {
            this.sensorStyles.styles = styleDifferences;
            const newSensorStyle = await this.sensorSheetStylesService.updateSlotStyle(this.sensorStyles);
            newSensorStyle.styles = currentStyles;
            this.sensorStyles = newSensorStyle;
        } catch (err) {
            this.sensorStyles.styles = currentStyles;
            throw err;
        }
    }

    private applySavedStyles(savedStyles: { [key: string]: string }, currentStyles: { [key: string]: string }) {
        const newStyles = _.cloneDeep(currentStyles);

        _.forEach(savedStyles, (savedCss, key) => {
            const currentCssObj = this.parseCss(newStyles[key] || '');
            const savedCssObj = this.parseCss(savedStyles[key]);
            const mergedCss = { ...currentCssObj, ...savedCssObj };
            newStyles[key] = this.cssObjToString(mergedCss);
        });

        return newStyles;
    }

    private parseCss(css: string): { [prop: string]: string } {
        const obj: any = {};
        css.split(';').forEach(decl => {
            if (!decl.trim()) return;
            const [prop, val] = decl.split(':').map(s => s.trim());
            if (prop && val) obj[prop] = val;
        });
        return obj;
    }

    private cssObjToString(obj: { [prop: string]: string }): string {
        return Object.entries(obj)
            .map(([k, v]) => `${k}: ${v}`)
            .join('; ');
    }

    private columnToLetter(column) {
        let letter = '';
        while (column > 0) {
            let remainder = column % 26;
            letter = String.fromCharCode(65 + remainder) + letter;
            column = Math.floor(column / 26) - 1;
        }
        return letter;
    }

    private getStylePropertyValue(selectedCellsStyles, styleKey, defaultValue) {
        const styles = Object.values(selectedCellsStyles);
        let value;

        styles.forEach((style: string) => {
            const stylesObject = this.parseCss(style);
            const styleEntry = stylesObject[styleKey];
            if (!styleEntry) {
                value = defaultValue;
            } else {
                value = styleEntry || defaultValue;
            }
        });

        return value;
    }

    private updateFamilyFont(selectedCellsStyles, spreadsheet) {
        const currentFont = this.getStylePropertyValue(
            selectedCellsStyles,
            ExcelStyleOptions.FontFamily,
            this.DEFAULT_FAMILY_FONT
        );

        const fontFamilySelector = spreadsheet.nativeElement.querySelector('[data-k="font-family"]');
        const fontFamilyOptions: any[] = fontFamilySelector.children;

        for (let i = 0; i < fontFamilyOptions.length; i++) {
            if (fontFamilyOptions[i].innerHTML == currentFont) {
                fontFamilyOptions[i].selected = true;
            } else {
                fontFamilyOptions[i].removeAttribute('selected');
            }
        }
    }

    private updateFontSize(selectedCellsStyles, spreadsheet) {
        const currentFont = this.getStylePropertyValue(
            selectedCellsStyles,
            ExcelStyleOptions.FontSize,
            this.DEFAULT_FONT_SIZE
        );

        const fontFamilySelector = spreadsheet.nativeElement.querySelector('[data-k="font-size"]');
        const fontFamilyOptions: any[] = fontFamilySelector.children;

        for (let i = 0; i < fontFamilyOptions.length; i++) {
            if (fontFamilyOptions[i].innerHTML == currentFont) {
                fontFamilyOptions[i].selected = true;
            } else {
                fontFamilyOptions[i].removeAttribute('selected');
            }
        }
    }

    private updateTextColor(selectedCellsStyles) {
        const textColor = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.Color);
        const colorTextIcon = document.getElementById('format_color_text');

        if (colorTextIcon) {
            if (textColor && textColor !== ExcelStyleOptions.Undefined) {
                colorTextIcon.style.color = textColor;
            } else {
                colorTextIcon.style.color = 'rgb(0, 0, 0)';
            }
        }
    }

    private updateCellColor(selectedCellsStyles) {
        const cellColor = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.BackgroundColor);
        const colorCellIcon = document.getElementById('format_color_fill');

        if (colorCellIcon) {
            if (cellColor && cellColor !== ExcelStyleOptions.Undefined) {
                colorCellIcon.style.color = cellColor;
            } else {
                colorCellIcon.style.color = 'rgb(255, 255, 255)';
            }
        }
    }

    private cellHasStyle(selectedCellsStyles, excelStyle) {
        return this.getStylePropertyValue(selectedCellsStyles, excelStyle, ExcelStyleOptions.Undefined);
    }

    private updateBoldToolbarStyle(selectedCellsStyles, spreadsheet) {
        const boldIsSelected = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.FontWeight);
        const boldSelector = spreadsheet.nativeElement.querySelector('#format_bold');

        if (boldSelector) {
            if (boldIsSelected && boldIsSelected !== ExcelStyleOptions.Undefined) {
                boldSelector.style.backgroundColor = 'lightgray';
            } else {
                boldSelector.style.removeProperty(ExcelStyleOptions.BackgroundColor);
            }
        }
    }

    private updateAlignToolbarStyle(selectedCellsStyles, spreadsheet) {
        const textAlign = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.TextAlign);

        const alignSelectors = {
            left: spreadsheet.nativeElement.querySelector('#align_left'),
            center: spreadsheet.nativeElement.querySelector('#align_center'),
            right: spreadsheet.nativeElement.querySelector('#align_right'),
        };

        Object.values(alignSelectors).forEach(selector => {
            if (selector) {
                selector.style.removeProperty(ExcelStyleOptions.BackgroundColor);
            }
        });

        if (textAlign && textAlign !== ExcelStyleOptions.Undefined) {
            const selectedAlignSelector = alignSelectors[textAlign];
            if (selectedAlignSelector) {
                selectedAlignSelector.style.backgroundColor = 'lightgray';
            }
        }
    }

    private getStylesDifferences(currentStyles: Record<string, string> | string): {
        [key: string]: string;
    } {
        if (!this.sensorStyles || !this.sensorStyles.styles) return {};

        const differences: { [key: string]: string } = {};
        const defaultStyles = this.defaultStyle;
        const allKeys = _.union(_.keys(defaultStyles), _.keys(currentStyles));
        allKeys.forEach(cell => {
            const savedCellStyle = this.cleanStyleString(defaultStyles[cell]);
            const currentCellStyle = this.cleanStyleString(currentStyles[cell]);
            if (!_.isEqual(savedCellStyle, currentCellStyle)) {
                const updatedStyles = currentCellStyle
                    .split(';')
                    .filter(style => !savedCellStyle.includes(style) && style)
                    .join(';');
                if (updatedStyles) {
                    differences[cell] = updatedStyles;
                }
            }
        });

        return differences;
    }

    private hasStyleDifference(currentStyles: Record<string, string> | string): boolean {
        if (!this.sensorStyles || !this.sensorStyles.styles) return false;
        if (!currentStyles) return;
        let hasDifferences = false;
        const savedGroupStyles = this.sensorStyles.styles;
        const allKeys = _.union(_.keys(savedGroupStyles), _.keys(currentStyles));
        for (const cell of allKeys) {
            if (hasDifferences) break;

            const savedCellStyle = this.cleanStyleString(savedGroupStyles[cell]);
            const currentCellStyle = this.cleanStyleString(currentStyles[cell]);
            if (currentCellStyle && !_.isEqual(savedCellStyle, currentCellStyle)) {
                const updatedStyles = currentCellStyle
                    .split(';')
                    .filter(style => !savedCellStyle?.includes(style) && style)
                    .join(';');
                if (updatedStyles) {
                    hasDifferences = true;
                }
            }
        }

        return hasDifferences;
    }

    private cleanStyleString(styleString) {
        if (styleString) {
            return styleString
                .split(';')
                .filter(style => !style.includes('overflow') && style)
                .join(';');
        }
    }
}

export enum ExcelStyleOptions {
    Undefined = 'undefined',
    Color = 'color',
    BackgroundColor = 'background-color',
    FontSize = 'font-size',
    FontFamily = 'font-family',
    TextAlign = 'text-align',
    FontWeight = 'font-weight',
}
